import { call, put, select } from 'redux-saga/effects';
import { Action } from '@reduxjs/toolkit';
import { selectFeatureFlag } from 'features/feature-set/featuresSlice';
import { selectIsSystemSession } from 'features/application/selectors';
import { selectIsAudioOnlyRoom } from 'features/room/roomSlice';
import { hasPermissionsSaga } from 'features/permissions/sagas/hasPermissionsSaga';
import { PermissionTypes } from 'features/permissions/types';
import {
  selectVbProviderV2Enabled,
  selectVirtualBackgroundDeviceSupported,
  selectVirtualBackgroundStatus,
} from 'features/virtual-backgrounds/selectors';
import { VirtualBackgroundsStatus } from 'features/virtual-backgrounds/types';
import { initVirtualBackground } from 'features/virtual-backgrounds/thunks/initVirtualBackground';

export function* preloadVirtualBackgroundSaga() {
  const status: VirtualBackgroundsStatus = yield select(selectVirtualBackgroundStatus);
  // initVirtualBackground's own condition also admits 'error', which would re-download the
  // model on every re-trigger once an init has failed.
  if (status !== 'idle') {
    return;
  }

  const vbEnabled: boolean = yield select(selectFeatureFlag, 'virtualBackgrounds');
  const v2Enabled: boolean = yield select(selectVbProviderV2Enabled);
  const isAudioOnlyRoom: boolean = yield select(selectIsAudioOnlyRoom);
  if (!vbEnabled || !v2Enabled || isAudioOnlyRoom) {
    return;
  }

  // The preload downloads the model and the ORT runtime (~3-5 MB) and calls the licence
  // endpoint, so skip participants who can never activate a background.
  const isSystemSession: boolean = yield select(selectIsSystemSession);
  const deviceSupported: boolean = yield select(selectVirtualBackgroundDeviceSupported);
  const canBroadcastVideo: boolean = yield call(hasPermissionsSaga, PermissionTypes.broadcastVideo);
  if (isSystemSession || !deviceSupported || !canBroadcastVideo) {
    return;
  }

  const thunkAction: Action = yield call(initVirtualBackground);
  yield put(thunkAction);
}
